import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { getPostLoginRedirect } from '../../lib/roleRoutes';

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX   = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const PIN6_REGEX  = /^[1-9][0-9]{5}$/;

const ROLES = [
  { value: 'buyer',       label: 'Sugar Buyer',              icon: '🛒' },
  { value: 'seller',      label: 'Sugar Seller (via Tender)', icon: '📦' },
  { value: 'factory',     label: 'Sugar Factory',            icon: '🏭' },
  { value: 'transporter', label: 'Transport Agency',         icon: '🚛' },
  { value: 'sales_agent', label: 'Sales Agent',              icon: '🤝' },
];

const GSTIN_ROLES = ['buyer', 'seller', 'factory'];
const PAN_ROLES   = ['transporter', 'sales_agent'];
const SUPABASE_FN = 'https://dfliwixthxiocdfzpbvt.supabase.co/functions/v1/pwa-user-signup';
const VERIFICO_BASE = 'https://dfliwixthxiocdfzpbvt.supabase.co/functions/v1';

// Official CBIC GST state code list — stable since GST rollout, used to
// translate the 2-digit code from GSTIN verification into a readable name
// for user_profiles.state (which expects a name, not the raw digits).
const GST_STATE_CODES = {
  '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
  '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi',
  '08': 'Rajasthan', '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim',
  '12': 'Arunachal Pradesh', '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram',
  '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal',
  '20': 'Jharkhand', '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh',
  '24': 'Gujarat', '25': 'Dadra and Nagar Haveli and Daman and Diu',
  '26': 'Dadra and Nagar Haveli and Daman and Diu', '27': 'Maharashtra',
  '28': 'Andhra Pradesh (Old)', '29': 'Karnataka', '30': 'Goa',
  '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu', '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands', '36': 'Telangana', '37': 'Andhra Pradesh',
  '38': 'Ladakh', '97': 'Other Territory', '99': 'Centre Jurisdiction',
};

const inp = {
  width: '100%', padding: '12px 14px',
  border: '1.5px solid #e5e7eb', borderRadius: 8,
  fontSize: 14, color: '#111827', background: '#fafaf9',
  outline: 'none', boxSizing: 'border-box',
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 700,
  color: '#6b7280', textTransform: 'uppercase',
  letterSpacing: 0.8, marginBottom: 6,
};

const getRouteForRole = (role) => {
  const r = (role || '').toLowerCase().trim().replace(/ /g, '_');
  if (r === 'buyer' || r === 'sugar_buyer') return '/buyer-operations-dashboard';
  if (r === 'seller' || r === 'quota_manager' || r === 'quotamanager' || r === 'sugar_quota_seller') return '/quota-allocation-pricing-center';
  if (r === 'factory') return '/factory-management-hub';
  if (r === 'transporter') return '/transporter-dashboard';
  if (r === 'sales_agent') return '/sales-agent-dashboard';
  if (r === 'admin') return '/admin-dashboard';
  if (r === 'monitor') return '/monitor-dashboard';
  return '/buyer-operations-dashboard';
};

export default function UserRegistration() {
  // Detect completion mode (Google OAuth user completing registration)
  const params  = new URLSearchParams(window.location.search);
  const isComplete = params.get('mode') === 'complete';
  const prefillEmail = params.get('email') || '';
  const prefillUid   = params.get('uid') || '';

  const [role, setRole]             = useState('buyer');
  const [gstin, setGstin]           = useState('');
  const [pan, setPan]               = useState('');
  const [phone, setPhone]           = useState('');
  const [email, setEmail]           = useState(prefillEmail);
  const [pincode, setPincode]       = useState('');
  const [pin, setPin]               = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin]       = useState(false);
  const [showPin2, setShowPin2]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState(false);

  // Ownership claim — shown when the entered GSTIN is already registered
  const [claimModalOpen, setClaimModalOpen]   = useState(false);
  const [claimType, setClaimType]             = useState('employee');
  const [claimName, setClaimName]             = useState('');
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimSubmitted, setClaimSubmitted]   = useState(false);
  const [claimError, setClaimError]           = useState('');

  const isGstinRole = GSTIN_ROLES.includes(role);
  const isPanRole   = PAN_ROLES.includes(role);

  const validate = () => {
    if (!phone.trim())                           return 'WhatsApp number is required';
    if (!/^\d{10}$/.test(phone.trim()))          return 'Enter a valid 10-digit WhatsApp number';
    if (!isComplete && !email.trim())            return 'Email is required';
    if (!isComplete && !pin)                     return 'PIN is required';
    if (!isComplete && !/^\d{4}$/.test(pin))    return 'PIN must be exactly 4 digits';
    if (!isComplete && pin !== confirmPin)       return 'PINs do not match';
    if (isPanRole && !pincode.trim())            return 'Pincode is required';
    if (isPanRole && !PIN6_REGEX.test(pincode)) return 'Enter a valid 6-digit pincode';
    if (isGstinRole) {
      if (!gstin)                                return 'GSTIN is required';
      if (!GSTIN_REGEX.test(gstin))             return 'Invalid GSTIN format (e.g. 29ABCDE1234F1Z5)';
    }
    if (isPanRole) {
      if (!pan)                                  return 'PAN is required';
      if (!PAN_REGEX.test(pan))                 return 'Invalid PAN format (e.g. ABCDE1234F)';
    }
    return null;
  };

  // Complete OAuth profile — call verify-gstin/verify-pan then update user_profiles directly
  const handleCompleteProfile = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setLoading(true);
    setError('');
    try {
      const gstin_clean = gstin ? gstin.trim().toUpperCase() : null;
      const pan_clean   = pan   ? pan.trim().toUpperCase()   : null;

      // Call Verifico via edge functions
      let verifData = {};
      let kyc_status = 'pending';
      let full_name = null;
      let company_name = null;

      if (isGstinRole && gstin_clean) {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${VERIFICO_BASE}/verify-gstin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ gstin: gstin_clean }),
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          setError('GSTIN verification failed: ' + (json.error || 'Please check your GSTIN'));
          setLoading(false);
          return;
        }
        verifData    = json;        
        kyc_status   = json.kyc_status || 'verified';
        company_name = verifData.legal_name || null;
        full_name    = verifData.legal_name || null;
      }

      if (isPanRole && pan_clean) {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${VERIFICO_BASE}/verify-pan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ pan: pan_clean }),
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          setError('PAN verification failed: ' + (json.error || 'Please check your PAN'));
          setLoading(false);
          return;
        }
        verifData  = json;
        kyc_status = json.kyc_status || 'verified';
        full_name  = verifData.full_name || null;
        company_name = full_name;
      }

      // Normalize phone — always store as +91XXXXXXXXXX
      let p = phone.trim().replace(/\D/g, ''); // digits only
      if (p.startsWith('91') && p.length === 12) p = p.substring(2);
      else if (p.startsWith('0') && p.length === 11) p = p.substring(1);
      const phoneNorm = '+91' + p;

      // Upsert user_profiles — handles both new and existing OAuth users
      const { error: upsertError } = await supabase
        .from('user_profiles')
        .upsert({
          user_id:       prefillUid,
          email:         prefillEmail.toLowerCase(),
          role,
          mobile_phone:  phoneNorm,
          company_name,
          full_name,
          gstin:         gstin_clean || null,
          pan:           pan_clean || null,
          pan_verified:  pan_clean ? true : false,
          address_line1: verifData.address || null,
          pincode:       verifData.pincode || pincode || null,
          state:         GST_STATE_CODES[verifData.state_code] || null,
          kyc_status,
          is_active:     true,
        }, { onConflict: 'user_id' });

      if (upsertError) {
        setError('Failed to save profile: ' + upsertError.message);
        setLoading(false);
        return;
      }

      // Insert company_accounts for GSTIN roles
      if (isGstinRole && gstin_clean) {
        await supabase.from('company_accounts').upsert({
          user_id:           prefillUid,
          role,
          gstin:             gstin_clean,
          legal_name:        verifData.legal_name || null,
          trade_name:        verifData.trade_name || null,
          gstin_status:      verifData.gstin_status || null,
          constitution:      verifData.constitution || null,
          tax_payer_type:    verifData.taxpayer_type || null,
          registration_date: verifData.date_of_registration || null,
          state_code:        verifData.state_code || null,
          pincode:           verifData.pincode || null,
          gstin_verified:    kyc_status === 'verified',
          gstin_verified_at: kyc_status === 'verified' ? new Date().toISOString() : null,
        }, { onConflict: 'user_id' });
      }

      // Send welcome email — fire and forget
      fetch('https://dfliwixthxiocdfzpbvt.supabase.co/functions/v1/send-welcome-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: prefillEmail.toLowerCase(), name: full_name, role }),
      }).catch(e => console.error('Welcome email error:', e));

      // Set localStorage
      localStorage.setItem('userRole',  role);
      localStorage.setItem('userEmail', prefillEmail.toLowerCase());
      localStorage.setItem('userName',  full_name || prefillEmail);
      localStorage.setItem('userId',    prefillUid);

      setSuccess(true);
      // Preserve a Mandi-originated (or any other) intended destination through
      // registration completion instead of always hard-redirecting to the role
      // dashboard — reuses the same ?next= validation as login-authentication.
      setTimeout(() => window.location.href = getPostLoginRedirect() || getRouteForRole(role), 1500);

    } catch (e) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Normal registration — call pwa-user-signup
  const handleRegister = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setLoading(true);
    setError('');
    try {
      const payload = {
        role,
        Phone:       phone.trim(),
        email:       email.trim(),
        pin,
        confirm_pin: confirmPin,
      };
      if (isGstinRole) payload.gstin   = gstin;
      if (isPanRole)   payload.pan     = pan;
      if (isPanRole)   payload.pincode = pincode.trim();

      const res  = await fetch(SUPABASE_FN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error_code === 'gstin_already_registered') {
          setClaimModalOpen(true);
        } else {
          setError(data.error || 'Registration failed. Please try again.');
        }
      } else {
        setSuccess(true);
        // Fresh mobile/PIN signup still requires a login step afterwards —
        // carry ?next through to it so the intended destination survives.
        const next = getPostLoginRedirect();
        const loginUrl = next ? `/login-authentication?next=${encodeURIComponent(next)}` : '/login-authentication';
        setTimeout(() => window.location.href = loginUrl, 2000);
      }
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClaim = async () => {
    if (!claimName.trim()) { setClaimError('Please enter your name'); return; }
    if (!phone.trim() || !/^\d{10}$/.test(phone.trim())) { setClaimError('Please enter a valid 10-digit WhatsApp number'); return; }
    setClaimSubmitting(true);
    setClaimError('');
    try {
      const res = await fetch(
        'https://dfliwixthxiocdfzpbvt.supabase.co/functions/v1/submit-ownership-claim',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            gstin: gstin.trim().toUpperCase(),
            claim_type: claimType,
            full_name: claimName.trim(),
            mobile: phone.trim(),
            email: email.trim(),
          }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        setClaimError(data.error || 'Could not submit your request. Please try again.');
        return;
      }
      setClaimSubmitted(true);
    } catch {
      setClaimError('Network error. Please check your connection.');
    } finally {
      setClaimSubmitting(false);
    }
  };

  const handleSubmit = isComplete ? handleCompleteProfile : handleRegister;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f0e8', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* Top bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5d9c2', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a href="/" style={{ color: '#1e4a72', textDecoration: 'none', fontSize: 13, fontWeight: 500 }}>← Home</a>
          <span style={{ color: '#d1d5db' }}>|</span>
          <span style={{ color: '#1e4a72', fontWeight: 700, fontSize: 15 }}>SugarHathi</span>
        </div>
        {!isComplete && (
          <a href="/login-authentication" style={{ color: '#1e4a72', textDecoration: 'none', fontSize: 13 }}>
            Have an account? <span style={{ color: '#d97706', fontWeight: 600 }}>Login →</span>
          </a>
        )}
      </div>

      {/* Card */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 16px' }}>
        <div style={{ width: '100%', maxWidth: 460, background: '#fff', borderRadius: 20, boxShadow: '0 4px 32px rgba(30,74,114,0.10)', border: '1px solid #e5d9c2', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #1e4a72, #15375a)', padding: '24px 28px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: '#d97706', textTransform: 'uppercase', marginBottom: 4 }}>SugarHathi</div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>
              {isComplete ? 'Complete Your Profile' : 'Create Your Account'}
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#a8c4d8' }}>
              {isComplete
                ? 'A few more details needed before you can start trading'
                : 'Factory-direct sugar trading platform'}
            </p>
          </div>

          <div style={{ padding: '24px 28px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Complete mode notice */}
            {isComplete && (
              <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, color: '#92400e', fontSize: 13 }}>
                🔐 You signed in with Google. Please complete your business profile to continue.
              </div>
            )}

            {/* Success */}
            {success && (
              <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, color: '#15803d', fontSize: 13, textAlign: 'center' }}>
                ✅ {isComplete ? 'Profile complete! Redirecting...' : 'Account created! Redirecting to login...'}
              </div>
            )}

            {/* Error */}
            {error && (
              <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
                ⚠️ {error}
              </div>
            )}

            {/* Role selector */}
            <div>
              <label style={lbl}>I am a...</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ROLES.map(r => (
                  <button key={r.value} type="button"
                    onClick={() => { setRole(r.value); setGstin(''); setPan(''); setPincode(''); setError(''); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px',
                      background: role === r.value ? '#eff6ff' : '#fafaf9',
                      border: role === r.value ? '2px solid #1e4a72' : '1.5px solid #e5e7eb',
                      borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                    }}>
                    <span style={{ fontSize: 18 }}>{r.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: role === r.value ? 600 : 400, color: role === r.value ? '#1e4a72' : '#374151' }}>
                      {r.label}
                    </span>
                    {role === r.value && <span style={{ marginLeft: 'auto', color: '#1e4a72' }}>✓</span>}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ height: 1, background: '#f0ebe0' }} />

            {/* GSTIN */}
            {isGstinRole && (
              <div>
                <label style={lbl}>GSTIN *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    value={gstin}
                    onChange={e => { setGstin(e.target.value.toUpperCase()); setError(''); }}
                    placeholder="e.g. 29ABCDE1234F1Z5"
                    maxLength={15}
                    style={{ ...inp, fontFamily: 'monospace', letterSpacing: 1, paddingRight: 36 }}
                  />
                  {gstin.length === 15 && GSTIN_REGEX.test(gstin) && (
                    <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#16a34a' }}>✓</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Verified against GSTN government database</div>
              </div>
            )}

            {/* PAN */}
            {isPanRole && (
              <div>
                <label style={lbl}>PAN Number *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    value={pan}
                    onChange={e => { setPan(e.target.value.toUpperCase()); setError(''); }}
                    placeholder="e.g. ABCDE1234F"
                    maxLength={10}
                    style={{ ...inp, fontFamily: 'monospace', letterSpacing: 1, paddingRight: 36 }}
                  />
                  {pan.length === 10 && PAN_REGEX.test(pan) && (
                    <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#16a34a' }}>✓</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Verified against NSDL government database</div>
              </div>
            )}

            {/* WhatsApp */}
            <div>
              <label style={lbl}>WhatsApp Number *</label>
              <input
                value={phone}
                onChange={e => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setError(''); }}
                placeholder="10-digit mobile number"
                type="tel"
                maxLength={10}
                style={inp}
              />
            </div>

            {/* Pincode — transporter / sales_agent only */}
            {isPanRole && (
              <div>
                <label style={lbl}>Business Pincode *</label>
                <input
                  value={pincode}
                  onChange={e => { setPincode(e.target.value); setError(''); }}
                  placeholder="e.g. 411001"
                  maxLength={6}
                  type="tel"
                  style={inp}
                />
              </div>
            )}

            {/* Email — hidden in complete mode, pre-filled and read-only */}
            {isComplete ? (
              <div>
                <label style={lbl}>Email Address</label>
                <input value={prefillEmail} readOnly style={{ ...inp, background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' }} />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Pre-filled from your Google account</div>
              </div>
            ) : (
              <div>
                <label style={lbl}>Email Address *</label>
                <input
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder="For booking confirmations"
                  type="email"
                  style={inp}
                />
              </div>
            )}

            {/* PIN — hidden in complete mode */}
            {!isComplete && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={lbl}>4-Digit PIN *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      value={pin}
                      onChange={e => { setPin(e.target.value); setError(''); }}
                      type={showPin ? 'text' : 'password'}
                      placeholder="••••"
                      maxLength={4}
                      style={{ ...inp, paddingRight: 36 }}
                    />
                    <button type="button" onClick={() => setShowPin(p => !p)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#9ca3af' }}>
                      {showPin ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
                <div>
                  <label style={lbl}>Confirm PIN *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      value={confirmPin}
                      onChange={e => { setConfirmPin(e.target.value); setError(''); }}
                      type={showPin2 ? 'text' : 'password'}
                      placeholder="••••"
                      maxLength={4}
                      style={{ ...inp, paddingRight: 36, borderColor: confirmPin && pin !== confirmPin ? '#fca5a5' : undefined }}
                    />
                    <button type="button" onClick={() => setShowPin2(p => !p)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#9ca3af' }}>
                      {showPin2 ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Verification badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8 }}>
              <span>🛡️</span>
              <span style={{ fontSize: 12, color: '#92400e' }}>
                {isGstinRole
                  ? 'Your GSTIN is verified with the government database. Business details are auto-captured.'
                  : 'Your PAN is verified with the government database. PAN details are auto-captured.'}
              </span>
            </div>

            {/* Submit */}
            <button type="button" onClick={handleSubmit} disabled={loading || success}
              style={{
                width: '100%', padding: '14px',
                background: loading ? '#9ca3af' : 'linear-gradient(135deg, #d97706, #b45309)',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                letterSpacing: 0.3,
              }}>
              {loading
                ? '⏳ Verifying & Saving...'
                : isComplete ? 'Complete Registration' : 'Create Account'}
            </button>

            {/* Bottom links */}
            {!isComplete && (
              <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>
                  Already have an account?{' '}
                  <a href="/login-authentication" style={{ color: '#1e4a72', fontWeight: 600, textDecoration: 'none' }}>Login here</a>
                </p>
                <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>
                  <a href="/" style={{ color: '#9ca3af', textDecoration: 'none' }}>← Back to Home</a>
                </p>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Ownership claim modal — shown when the entered GSTIN is already registered */}
      {claimModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(30,41,59,0.55)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{
            width: '100%', maxWidth: 460, background: '#fff',
            borderRadius: '20px 20px 0 0', overflow: 'hidden',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.2)',
          }}>
            <div style={{ background: 'linear-gradient(135deg, #1e4a72, #15375a)', padding: '22px 26px 18px' }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%', background: 'rgba(217,119,6,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 10,
              }}>🏢</div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#fff' }}>
                This GSTIN is Already Registered
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#a8c4d8', lineHeight: 1.5 }}>
                This business already has a SugarHathi account. Are you an employee or a partner/director of this firm?
              </p>
            </div>

            <div style={{ padding: '20px 26px 26px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {claimSubmitted ? (
                <>
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 16px',
                    background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10,
                  }}>
                    <span style={{ fontSize: 18 }}>✅</span>
                    <span style={{ fontSize: 12.5, color: '#15803d', lineHeight: 1.6 }}>
                      Request submitted. Our support team will look into this — please allow up to 2 working days.
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setClaimModalOpen(false); setClaimSubmitted(false); setClaimName(''); }}
                    style={{
                      width: '100%', padding: '12px', background: '#f3f4f6', color: '#374151',
                      border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    }}>
                    Close
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setClaimType('employee')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                      borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                      border: claimType === 'employee' ? '2px solid #d97706' : '1.5px solid #e5e7eb',
                      background: claimType === 'employee' ? '#fffbeb' : '#fafaf9',
                    }}>
                    <span style={{ fontSize: 20 }}>🧑‍💼</span>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1e4a72' }}>I'm an Employee</div>
                      <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 1 }}>I work here, not an owner or director</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setClaimType('partner')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                      borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                      border: claimType === 'partner' ? '2px solid #d97706' : '1.5px solid #e5e7eb',
                      background: claimType === 'partner' ? '#fffbeb' : '#fafaf9',
                    }}>
                    <span style={{ fontSize: 20 }}>🤝</span>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1e4a72' }}>I'm a Partner / Director</div>
                      <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 1 }}>I'm listed on this business's official records</div>
                    </div>
                  </button>

                  <div>
                    <label style={lbl}>Your Full Name *</label>
                    <input
                      value={claimName}
                      onChange={e => { setClaimName(e.target.value); setClaimError(''); }}
                      placeholder="As per your ID"
                      style={inp}
                    />
                  </div>

                  {claimError && (
                    <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
                      ⚠️ {claimError}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleSubmitClaim}
                    disabled={claimSubmitting}
                    style={{
                      width: '100%', padding: '14px',
                      background: claimSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #d97706, #b45309)',
                      color: '#fff', border: 'none', borderRadius: 10,
                      fontSize: 14.5, fontWeight: 700, marginTop: 6,
                      cursor: claimSubmitting ? 'not-allowed' : 'pointer',
                    }}>
                    {claimSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setClaimModalOpen(false)}
                    disabled={claimSubmitting}
                    style={{
                      width: '100%', padding: '10px', background: 'none', color: '#9ca3af',
                      border: 'none', fontSize: 12.5, cursor: 'pointer',
                    }}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



